import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { Pathname } from 'react-router-dom';

import colors from '../../../constants/colors';

interface OutlinedButtonProps extends LoadingButtonProps {
  width: number | string;
  // Must set a width because the border thickness makes it behave weird on hover
  // without a set width
}

export const OutlinedButton = (props: OutlinedButtonProps) => (
  <Button
    variant="outlined"
    color="inherit"
    {...props}
    sx={{
      color: colors.csaaBlack,
      borderColor: colors.csaaBlack,
      whiteSpace: 'nowrap',
      borderRadius: 1,
      width: { mobile: 1, tablet: props.fullWidth ? 1 : props.width },
      '&:hover': {
        borderRadius: 1,
        borderWidth: 2,
      },
      ...props.sx,
    }}
  />
);

export const TextButton = (
  props: LoadingButtonProps & { component?: React.ElementType; to?: Pathname }
) => {
  const theme = useTheme();
  return (
    <LoadingButton
      variant="text"
      disableElevation
      {...props}
      sx={{
        width: 'auto',
        textTransform: 'none',
        fontSize: { mobile: 16, tablet: 18 },
        lineHeight: '24px',
        fontWeight: props.disabled ? 400 : 700,
        minWidth: 'auto',
        color: theme.palette.primary.main,
        backgroundColor: colors.csaaWhite,
        p: 0,
        '&:hover': {
          backgroundColor: colors.blackInteractiveButtonHover,
        },
        ...props.sx,
      }}
    >
      {props.children}
    </LoadingButton>
  );
};

const Button = <C extends React.ElementType>(
  props: LoadingButtonProps<C, { component?: C; to?: Pathname }>
) => {
  const disabledProps = props.disabled
    ? { to: undefined, component: undefined }
    : {}; // this solves the AQA problems when using link buttons.

  return (
    <LoadingButton
      variant="contained"
      disableElevation
      disableRipple
      {...props}
      {...disabledProps}
      sx={{
        textTransform: 'none',
        height: 42,
        width: { mobile: 1, tablet: props.fullWidth ? 1 : 'auto' },
        fontSize: { mobile: 16, tablet: 18 },
        py: { mobile: 1 },
        px: [null, 4, 4],
        fontWeight: props.disabled ? 400 : 700,
        alignSelf: 'flex-end',
        whiteSpace: 'nowrap',
        ...props.sx,
      }}
    >
      {props.children}
    </LoadingButton>
  );
};

export default Button;
